import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import ScheduleTableCell from './ScheduleTableCell';
import { CalendarTableCell, CalendarYearTableCell, ClickableText, ColoredCircle, PaymentPlanTableCell } from './OfferPaymentPlanStyles';
import { Button, Typography } from '@totalsoft/rocket-ui';
import OfferPaymentPlanParamsCalendarOptions from './OfferPaymentPlanParamsCalendarOptions';

function OfferPaymentPlanParamsCalendarView({
  fixedTableColumns,
  scheduleCellParamColumns,
  scheduleCellParamValues,
  handleRowPropertyChange,
  handleRowsPropertyChange,
  getDynamicInputParams,
  onReset,
  selectedOptions,
  setSelectedOptions,
  handleMonthClick,
  handleYearClick,
  handleAllMonthsSelection,
  onResetSelectedCells,
  isEditable
}) {
  const { t } = useTranslation();

  const months = Array.from(Array(12).keys());
  const years = [...new Set(scheduleCellParamValues?.map(document => new Date(document.MonthDate).getFullYear()))];
  const selectedScheduleCells = scheduleCellParamValues?.filter(scheduleCells => scheduleCells['Selected'] === true);

  return (
    <>
      <Grid container mt={1} mb={2} gap={2} justifyContent='space-between'>
        <Grid item container xs={12} xl gap={1.5} order={{ xs: 2, xl: 1 }}>
          {scheduleCellParamColumns?.map((cellParam, index) => (
            <Grid key={cellParam.cellParamId} item container xs='auto' gap={0.5} alignItems='center'>
              <ColoredCircle colorIndex={index} />
              <Typography color='textSecondary' emphasis='bold'>
                {cellParam.cellParamDisplayName}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Grid item container xs={12} xl='auto' gap={1} alignItems='center' justifyContent='flex-end' order={{ xs: 1, xl: 2 }}>
          <Button color='primary' variant='outlined' onClick={onReset} disabled={!isEditable}>
            {t('OfferEdit.PaymentPlan.ClearAll')}
          </Button>
          <Button color='primary' variant='outlined' onClick={handleAllMonthsSelection(true)} disabled={!isEditable}>
            {t('OfferEdit.PaymentPlan.SelectAll')}
          </Button>
          <Button color='primary' variant='outlined' onClick={handleAllMonthsSelection(false)} disabled={!isEditable}>
            {t('OfferEdit.PaymentPlan.SelectNone')}
          </Button>
        </Grid>
      </Grid>
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <PaymentPlanTableCell></PaymentPlanTableCell>
              {months.map(month => (
                <PaymentPlanTableCell key={t(`DashboardIndicators.Months.${[month]}`)} align='center'>
                  <ClickableText color='primary' emphasis='bold' onClick={handleMonthClick(month)}>
                    {t(`DashboardIndicators.Months.${[month]}`)}
                  </ClickableText>
                </PaymentPlanTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {years.map(year => (
              <TableRow key={year}>
                <CalendarYearTableCell align='center'>
                  <ClickableText color='primary' emphasis='bold' onClick={handleYearClick(year)}>
                    {year}
                  </ClickableText>
                </CalendarYearTableCell>
                {months.map(month => (
                  <CalendarTableCell key={`${year}_${month}`} align='center' sx={{ minWidth: '86px', maxWidth: '86px' }}>
                    {scheduleCellParamValues?.map((document, idx) => {
                      if (new Date(document.MonthDate).getFullYear() === year && new Date(document.MonthDate).getMonth() === month)
                        return (
                          <ScheduleTableCell
                            rowIdx={idx}
                            cellData={document}
                            fixedTableColumns={fixedTableColumns}
                            handleRowPropertyChange={handleRowPropertyChange}
                            scheduleCellParamColumns={scheduleCellParamColumns}
                            selectedOptions={selectedOptions}
                          />
                        );
                    })}
                  </CalendarTableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {scheduleCellParamColumns && scheduleCellParamValues && selectedOptions && (
        <OfferPaymentPlanParamsCalendarOptions
          scheduleCellParamColumns={scheduleCellParamColumns}
          selectedScheduleCells={selectedScheduleCells}
          getDynamicInputParams={getDynamicInputParams}
          handleRowsPropertyChange={handleRowsPropertyChange}
          isEditable={isEditable}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          onResetSelectedCells={onResetSelectedCells}
        />
      )}
    </>
  );
}

OfferPaymentPlanParamsCalendarView.propTypes = {
  fixedTableColumns: PropTypes.array.isRequired,
  scheduleCellParamColumns: PropTypes.array,
  scheduleCellParamValues: PropTypes.array,
  handleRowPropertyChange: PropTypes.func.isRequired,
  handleRowsPropertyChange: PropTypes.func.isRequired,
  getDynamicInputParams: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  selectedOptions: PropTypes.array.isRequired,
  setSelectedOptions: PropTypes.func.isRequired,
  handleMonthClick: PropTypes.func.isRequired,
  handleYearClick: PropTypes.func.isRequired,
  handleAllMonthsSelection: PropTypes.func.isRequired,
  onResetSelectedCells: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired
};

export default OfferPaymentPlanParamsCalendarView;
